import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, type Column } from "@legal-500/legal500-design-system";
import { powerlistRegions, type EditionRegion } from "../../../api/powerlist.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { ErrorBanner } from "../../../components/ErrorBanner/ErrorBanner";
import LoadingState from "../../../components/LoadingState";
import { useQuery } from "../../../hooks/useApi";
import { regionNodeKey } from "../../../hooks/useRegions";
import {
  RegionNodeSelect,
  regionNodeLabel,
  useRegionNodeNames,
  type RegionNodeRef,
} from "../RegionNodeSelect";
import { ConfirmModal, FormError, errorMessage, usePowerlistAccess, useToast } from "../shared";
import styles from "../Powerlist.module.css";

type MappingRow = EditionRegion & { key: string };

function mappingKey(region: EditionRegion): string {
  return regionNodeKey({ kind: region.regionKind, id: region.regionRefId });
}

type LabelModalProps = {
  region: EditionRegion | null;
  onClose: () => void;
  onSaved: () => void;
};

function EditLabelModal({ region, onClose, onSaved }: LabelModalProps) {
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLabel(region?.displayLabel ?? "");
    setError(null);
  }, [region]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!region) return;
    if (!label.trim()) {
      setError("Display label is required");
      return;
    }
    try {
      await powerlistRegions.update(region, label.trim());
      onSaved();
    } catch (saveError) {
      setError(errorMessage(saveError));
    }
  }

  return (
    <Modal isOpen={region !== null} onClose={onClose} title="Edit display label" size="small">
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <FormError message={error} />
        <Input
          id="powerlist-mapping-label-edit"
          label="Display label"
          value={label}
          onChange={event => setLabel(event.target.value)}
          fullWidth
        />
        <div className={styles.formActions}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function PageMappingsTab({ editionId }: { editionId: string }) {
  const { canWrite, canDelete } = usePowerlistAccess();
  const names = useRegionNodeNames();
  const { data, loading, error, refetch, isRefetching } = useQuery(
    () => powerlistRegions.list(editionId),
    [editionId],
  );
  const [selected, setSelected] = useState<RegionNodeRef | null>(null);
  const [label, setLabel] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditionRegion | null>(null);
  const [removing, setRemoving] = useState<EditionRegion | null>(null);
  const { toast, success } = useToast();

  const regions = useMemo<MappingRow[]>(
    () => (data ?? []).map(region => ({ ...region, key: mappingKey(region) })),
    [data],
  );
  const mapped = useMemo(() => new Set(regions.map(mappingKey)), [regions]);

  function pageName(region: EditionRegion): string {
    const node = names.get(mappingKey(region));
    return node
      ? regionNodeLabel(node)
      : `${region.regionKind === "region" ? "Region" : "Country"} ${region.regionRefId}`;
  }

  async function handleAdd(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected || !label.trim()) {
      setFormError("Choose a page and enter a display label");
      return;
    }
    setFormError(null);
    try {
      await powerlistRegions.create(editionId, {
        regionKind: selected.kind,
        regionRefId: selected.id,
        displayLabel: label.trim(),
      });
      setSelected(null);
      setLabel("");
      success("Page mapping added");
      await refetch();
    } catch (addError) {
      setFormError(errorMessage(addError));
    }
  }

  const columns: Column<MappingRow>[] = [
    {
      header: "Page",
      accessor: "regionRefId",
      render: (_value: unknown, row: MappingRow) => pageName(row),
    },
    { header: "Display label", accessor: "displayLabel" },
    ...(canWrite || canDelete
      ? [
          {
            header: "Actions",
            accessor: "editionId" as const,
            render: (_value: unknown, row: MappingRow) => (
              <div className={styles.actions}>
                {canWrite && (
                  <Button
                    variant="outlined"
                    size="small"
                    aria-label={`Edit label for ${row.displayLabel}`}
                    onClick={() => setEditing(row)}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    aria-label={`Remove ${row.displayLabel}`}
                    onClick={() => setRemoving(row)}
                  >
                    Remove
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.panel}>
      <p className={styles.muted}>
        The jurisdiction pages that show this edition. The display label is used on cards and in the
        Powerlist dropdown.
      </p>
      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}
      {loading && data === null ? (
        <LoadingState message="Loading page mappings..." />
      ) : (
        <DataTable
          data={regions}
          columns={columns}
          rowKey="key"
          emptyMessage="This edition is not mapped to any page yet."
        />
      )}

      {canWrite && (
        <form className={styles.inlineForm} onSubmit={handleAdd} noValidate>
          <RegionNodeSelect
            id="powerlist-mapping-region"
            value={selected}
            exclude={mapped}
            onChange={node => {
              setSelected(node);
              if (node && !label.trim()) setLabel(node.name ?? "");
            }}
          />
          <Input
            id="powerlist-mapping-label"
            label="Display label"
            value={label}
            onChange={event => setLabel(event.target.value)}
            fullWidth
          />
          <Button type="submit" variant="contained" color="primary" size="small">
            Add mapping
          </Button>
          <FormError message={formError} />
        </form>
      )}

      <EditLabelModal
        region={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          success("Display label saved");
          void refetch();
        }}
      />
      <ConfirmModal
        isOpen={removing !== null}
        title="Remove page mapping"
        message={`Stop showing this edition on ${removing ? pageName(removing) : "this page"}?`}
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return;
          await powerlistRegions.remove(removing);
          setRemoving(null);
          success("Page mapping removed");
          await refetch();
        }}
      />
      {toast}
    </div>
  );
}
